'use client';

import {
  ComponentProps,
  CSSProperties,
  FC,
  HTMLAttributes,
  memo,
  MouseEvent,
  PointerEvent,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { isFilled } from '@prismicio/client';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import classNames from 'classnames';

import { PAGE_ROUTES } from '@/constants';
import { useUiStoreSelector } from '@/data';
import {
  useFeaturedFlag,
  useHeaderMobilePositionControl,
  useIsMobile,
  useIsServerSide,
} from '@/hooks';
import { useData } from '../DataProvider';
import { AccountMenu } from '../AccountMenu';
import { IconSvg } from '../IconSvg';
import { useAuth } from '../Auth';
import { ShoppingHeader } from '../ShoppingHeader';
import { FavoriteHeader } from '../FavoriteHeader';
import { Container } from '../Container';
import { Nav } from '../Nav';

import style from './header.module.scss';
import { PrismicImageWithAlt } from '../PrismicImageWithAlt';
import { getHeaderAction, NavigationViewport } from '@/utils';
import { HeaderSignInCta } from './HeaderSignInCta';
import { resolveBackgroundImageToken } from '@/theme/tokens';

const BREAKPOINT = 1024;

const HeaderContainer: FC<PropsWithChildren> = ({ children }) => {
  const pathname = usePathname();
  const { header } = useData();
  const { isMobile } = useIsMobile(BREAKPOINT);
  const headerRef = useRef<HTMLElement | null>(null);
  const [applyBackground, setApplyBackground] = useState(false);

  const { activeDesktop, activeMobile } = header.data;

  const cleanPath = pathname?.replace(/\/$/, '');
  const isHome = pathname === '/' || cleanPath === '/home';

  const headerDesktopFloating = !!activeDesktop.primary.floating && isHome;
  const headerMobileFloating = !!activeMobile.primary.floating && isHome;
  const headerFloating = isMobile
    ? headerMobileFloating
    : headerDesktopFloating;
  const headerFilled = isMobile
    ? !!activeMobile.primary.floating && !isHome
    : !!activeDesktop.primary.floating && !isHome;

  const backgroundImage = header.data.background_image;
  const navBackgroundImage = header.data.nav_background_image;
  const desktopBackgroundUrl = isFilled.image(backgroundImage)
    ? backgroundImage.url
    : undefined;
  const mobileBackgroundUrl = backgroundImage?.mobile?.url || undefined;
  const desktopNavBackgroundUrl =
    navBackgroundImage && isFilled.image(navBackgroundImage)
      ? navBackgroundImage.url
      : undefined;
  const mobileNavBackgroundUrl = navBackgroundImage?.mobile?.url || undefined;
  const headerStyleVars: Record<string, string> = {};
  const desktopStyle = header.data.background_image_style;
  const mobileStyle = header.data.background_image_style_mobile;
  const desktopHidesImage = desktopStyle === 'None';
  const mobileSameAsDesktop = !mobileStyle || mobileStyle === 'Same as desktop';
  const mobileHidesImage =
    mobileStyle === 'None' || (mobileSameAsDesktop && desktopHidesImage);
  const navHidesImage = header.data.nav_background_image_style === 'None';
  const showHeaderImageDesktop =
    Boolean(desktopBackgroundUrl) && !desktopHidesImage;
  const showHeaderImageMobile =
    Boolean(mobileBackgroundUrl || desktopBackgroundUrl) && !mobileHidesImage;
  const showNavImageDesktop =
    Boolean(desktopNavBackgroundUrl) && !navHidesImage;
  const showNavImageMobile =
    Boolean(mobileNavBackgroundUrl || desktopNavBackgroundUrl) &&
    !navHidesImage;
  const clearNavDesktop = !showNavImageDesktop && showHeaderImageDesktop;
  const clearNavMobile = !showNavImageMobile && showHeaderImageMobile;
  const headerBackgroundImageClass = resolveBackgroundImageToken(desktopStyle);
  // Same as desktop inherits desktop styles, but None must also apply the
  // mobile none class so theme fallbacks still win below the lg breakpoint.
  const headerBackgroundImageMobileClass = resolveBackgroundImageToken(
    mobileSameAsDesktop
      ? desktopHidesImage
        ? 'None'
        : undefined
      : mobileStyle,
    { mobile: true },
  );

  if (
    desktopBackgroundUrl &&
    (showHeaderImageDesktop || (showHeaderImageMobile && !mobileBackgroundUrl))
  ) {
    headerStyleVars['--header-bg-image'] = `url("${desktopBackgroundUrl}")`;
  }

  if (mobileBackgroundUrl && showHeaderImageMobile) {
    headerStyleVars[
      '--header-bg-image-mobile'
    ] = `url("${mobileBackgroundUrl}")`;
  }

  if (desktopNavBackgroundUrl && showNavImageDesktop) {
    headerStyleVars['--nav-bg-image'] = `url("${desktopNavBackgroundUrl}")`;
  }

  if (showNavImageMobile && mobileNavBackgroundUrl) {
    headerStyleVars[
      '--nav-bg-image-mobile'
    ] = `url("${mobileNavBackgroundUrl}")`;
  }

  if (clearNavDesktop && clearNavMobile) {
    headerStyleVars['--nav-bg-image'] = 'none';
    headerStyleVars['--nav-bg-color'] = 'transparent';
    headerStyleVars['--nav-bg-image-mobile'] = 'none';
  }

  const headerStyle =
    Object.keys(headerStyleVars).length > 0
      ? (headerStyleVars as CSSProperties)
      : undefined;

  // ResizeObserver to update --header-height when the dimensions of the header change
  useEffect(() => {
    if (!headerRef.current) return;

    const updateHeaderHeight = (height: number) => {
      document.documentElement.style.setProperty(
        '--header-height',
        `${height || 0}px`,
      );
    };
    const headerHeight = headerRef?.current?.clientHeight || 0;
    // Initial call
    updateHeaderHeight(headerHeight);

    // Create ResizeObserver to detect changes in dimensions
    const resizeObserver = new ResizeObserver(entries => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          const headerHeight = entry.target.clientHeight || 0;
          updateHeaderHeight(headerHeight);
        }
      });
    });

    // Observe the header element
    resizeObserver.observe(headerRef.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    // Set header-offset to 0 if not floating
    if (!headerFloating) {
      document.documentElement.style.setProperty('--header-offset', '0px');
    }

    const updateHeaderState = () => {
      const scrollY = window.scrollY;

      // Update background based on scroll (always executes)
      if (headerFloating) {
        const raw = getComputedStyle(document.documentElement)
          .getPropertyValue('--top-promotion')
          .trim();
        const parsed = parseFloat(raw);
        const offset = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
        const shouldHidePromotion = scrollY > offset;
        setApplyBackground(shouldHidePromotion);

        // Update header-offset only when floating
        document.documentElement.style.setProperty(
          '--header-offset',
          shouldHidePromotion || headerFilled ? '0px' : `${offset}px`,
        );
      } else {
        // When not floating, apply background based on scroll position
        setApplyBackground(scrollY > 0);
      }
    };

    const handleScroll = () => {
      requestAnimationFrame(updateHeaderState);
    };

    // Initial call (same tick as Promotion useLayoutEffect: DOM order runs promo first)
    updateHeaderState();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [headerFloating, headerFilled]);

  useHeaderMobilePositionControl({
    enabled: headerMobileFloating,
    headerRef,
  });

  return (
    <header
      className={classNames(
        style.header,
        headerBackgroundImageClass,
        headerBackgroundImageMobileClass,
        {
          [style.header_floating]: headerDesktopFloating,
          [style.header_mobile_floating]: headerMobileFloating,
          [style.header_change_background]: applyBackground || headerFilled,
          [style.nav_bg_clear]: clearNavDesktop && !clearNavMobile,
          [style.nav_bg_clear_mobile]: clearNavMobile && !clearNavDesktop,
        },
      )}
      id="header"
      ref={headerRef}
      style={headerStyle}
    >
      {children}
    </header>
  );
};

const HeaderMobile: FC<PropsWithChildren> = ({ children }) => {
  const { isMobile } = useIsMobile(BREAKPOINT);
  const { isServerSide } = useIsServerSide();

  if (!isMobile && !isServerSide) return null;

  return <div className={classNames(style.header_mobile)}>{children}</div>;
};

const HeaderDesktop: FC<PropsWithChildren> = ({ children }) => {
  const { isMobile } = useIsMobile(BREAKPOINT);
  const { isServerSide } = useIsServerSide();

  if (isMobile && !isServerSide) return null;

  return <div className={classNames(style.header_desktop)}>{children}</div>;
};

type HeaderRowChild = ReactElement<HeaderSectionProps>;

const HeaderRow: FC<
  HTMLAttributes<HTMLDivElement> & {
    children: HeaderRowChild | HeaderRowChild[];
    type?:
      | 'flat'
      | 'custom'
      | 'small'
      | 'medium'
      | 'large'
      | 'small-bottom'
      | 'extra-small';
    borderless?: boolean;
    withNavbar?: boolean;
    fullWidth?: boolean;
    relativePosition?: boolean;
    differentBackground?: boolean;
  }
> = ({
  children,
  className,
  type = 'medium',
  borderless,
  withNavbar,
  fullWidth = false,
  relativePosition = false,
  differentBackground = false,
  ...props
}) => {
  const { header } = useData();
  const { isMobile } = useIsMobile(BREAKPOINT);
  const activeSlice = isMobile
    ? header.data.activeMobile
    : header.data.activeDesktop;
  const headerFloating = activeSlice.primary.floating;
  const navBackgroundImageClass = withNavbar
    ? resolveBackgroundImageToken(header.data.nav_background_image_style)
    : undefined;

  return (
    <div
      className={classNames(
        style.header_row,
        style[type],
        className,
        navBackgroundImageClass,
        {
          [style.borderless]: borderless,
          [style.with_navbar]: withNavbar,
          [style.with_navbar_sticky]: withNavbar && headerFloating,
          [style.position]: !!relativePosition,
          [style.different_background]: differentBackground,
        },
      )}
      {...props}
    >
      {fullWidth ? (
        children
      ) : (
        <Container className={style.header_row__container}>
          {children}
        </Container>
      )}
    </div>
  );
};

type HeaderSectionProps = PropsWithChildren<ComponentProps<'div'>> & {
  justify: 'start' | 'center' | 'end';
  spaceBetweenOnMultipleChildren?: boolean;
};

const HeaderSection: FC<HeaderSectionProps> = ({
  children,
  className,
  justify,
  spaceBetweenOnMultipleChildren,
  ...props
}) => (
  <div
    className={classNames(style.header_section, className, {
      [style.space_between]: spaceBetweenOnMultipleChildren,
    })}
    data-justify={justify}
    {...props}
  >
    {children}
  </div>
);

const HeaderLogo: FC<{
  fullWidth?: boolean;
  fixHeight?: boolean;
  center?: boolean;
}> = memo(({ fullWidth, fixHeight, center }) => {
  const { header } = useData();
  const { isMobile } = useIsMobile(1024);

  const logo =
    isMobile && header?.data?.mobile_logo?.url
      ? header?.data?.mobile_logo
      : header?.data?.logo;

  return (
    <Link
      aria-label={'Go to home'}
      className={classNames(style.logo, {
        [style.logo__fill]: !!fullWidth,
        [style.logo__fix_height]: !!fixHeight,
        [style.logo__center]: !!center,
      })}
      href={header?.data?.logo_url || '/'}
    >
      <figure className={style.logo__main}>
        <PrismicImageWithAlt
          defaultAlt="Brand logo"
          field={logo}
          height={logo?.dimensions?.height}
          quality={100}
          style={{
            objectFit: 'contain',
            width: '100%',
            height: 'auto',
          }}
          width={logo?.dimensions?.width}
        />
      </figure>
    </Link>
  );
});

interface HeaderProfileProps {
  showLabel?: boolean;
  label?: string;
}

const HeaderProfile: FC<HeaderProfileProps> = ({
  showLabel,
  label = 'Sign In',
}) => {
  const { store, header } = useData();
  const { loading, isAuth } = useAuth();
  const router = useRouter();

  const handleClickLogIcon = () => {
    if (loading) {
      return;
    }
    if (!isAuth) {
      router.push(PAGE_ROUTES.SIGNIN);
      return;
    }

    router.push(PAGE_ROUTES.ACCOUNT);
  };

  if (!store?.account_setting?.active) return null;

  const signInCta = getHeaderAction(header, 'popover_account_cta');

  if (signInCta?.primary.active && !isAuth) {
    return (
      <HeaderSignInCta
        cta={signInCta.primary}
        label={label}
        showLabel={showLabel}
      />
    );
  }

  const profileLabel = isAuth ? 'Open account menu' : `Go to ${label} page`;

  return (
    <>
      <button
        aria-label={profileLabel}
        className={style.profile__action}
        onClick={handleClickLogIcon}
        type="button"
      >
        <IconSvg decorative={false} name="profile" title={profileLabel} />
        {showLabel && (
          <span className={style.profile__action_label}>
            {isAuth ? 'Profile' : label}
          </span>
        )}
      </button>
      <AccountMenu />
    </>
  );
};

const HeaderShopping = () => {
  const { isGRLimitActive } = useFeaturedFlag();

  if (isGRLimitActive) return null;

  return <ShoppingHeader />;
};

interface HeaderFavoritesProps {
  showLabel?: boolean;
  label?: string;
}

const HeaderFavorites: FC<HeaderFavoritesProps> = ({ ...props }) => {
  const { wishlistActive } = useFeaturedFlag();

  if (!wishlistActive) return null;

  return <FavoriteHeader {...props} />;
};

interface HeaderNavProps {
  viewport?: NavigationViewport;
}

const HeaderNav: FC<HeaderNavProps> = ({ viewport = 'desktop' }) => {
  const isMobile = viewport === 'mobile';
  return (
    <Nav
      className={classNames({
        [style.section__nav]: !isMobile,
        [style.section__nav_mobile]: isMobile,
      })}
      viewport={viewport}
    />
  );
};

const SCROLL_EDGE = 1;
const DRAG_THRESHOLD = 5;

const scrollDelta = (event: WheelEvent, pageSize: number) => {
  const dominant =
    Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;

  if (event.deltaMode === 1) return dominant * 16;
  if (event.deltaMode === 2) return dominant * pageSize;

  return dominant;
};

const HeaderNavDesktop = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startScroll: 0,
  });
  const [showStartFade, setShowStartFade] = useState(false);
  const [showEndFade, setShowEndFade] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    setShowStartFade(el.scrollLeft > SCROLL_EDGE);
    setShowEndFade(maxScroll - el.scrollLeft > SCROLL_EDGE);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (event: WheelEvent) => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) return;

      const next = Math.min(
        maxScroll,
        Math.max(0, el.scrollLeft + scrollDelta(event, el.clientWidth)),
      );
      if (next === el.scrollLeft) return;

      event.preventDefault();
      el.scrollLeft = next;
    };

    const onDragStart = (event: DragEvent) => {
      event.preventDefault();
    };

    updateFades();
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('dragstart', onDragStart);
    window.addEventListener('resize', updateFades);

    const observer = new ResizeObserver(updateFades);
    observer.observe(el);
    const nav = el.querySelector('nav');
    if (nav) observer.observe(nav);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('dragstart', onDragStart);
      window.removeEventListener('resize', updateFades);
      observer.disconnect();
    };
  }, [updateFades]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const el = scrollRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;

    dragRef.current = {
      active: true,
      moved: false,
      startX: event.clientX,
      startScroll: el.scrollLeft,
    };
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const el = scrollRef.current;
    if (!drag.active || !el) return;

    const distance = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(distance) < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      el.setPointerCapture(event.pointerId);
    }

    el.scrollLeft = drag.startScroll - distance;
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!dragRef.current.active) return;

    dragRef.current.active = false;
    if (el?.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
  };

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!dragRef.current.moved) return;

    dragRef.current.moved = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div className={style.nav_scroll_wrap} data-nav-desktop>
      <div
        className={classNames(style.nav_scroll, {
          [style.nav_scroll_draggable]: showStartFade || showEndFade,
        })}
        onClickCapture={onClickCapture}
        onPointerCancel={endDrag}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onScroll={updateFades}
        ref={scrollRef}
      >
        <HeaderNav />
      </div>
      {showStartFade && (
        <span aria-hidden="true" className={style.fade_start} />
      )}
      {showEndFade && <span aria-hidden="true" className={style.fade_end} />}
    </div>
  );
};

const HeaderBurgerNav = () => {
  const { setState } = useUiStoreSelector([]);

  return (
    <button
      aria-expanded="false"
      aria-label="Open main navigation menu"
      className={classNames(style.profile__action, style.nav__action)}
      onClick={() => setState({ is_nav_open: true })}
      type="button"
    >
      <IconSvg
        decorative={false}
        name="nav"
        title="Open main navigation menu"
      />
    </button>
  );
};

const Header = Object.assign(HeaderContainer, {
  Mobile: HeaderMobile,
  Desktop: HeaderDesktop,
});

export {
  Header as HeaderContainer,
  HeaderRow,
  HeaderSection,
  HeaderLogo,
  HeaderProfile,
  HeaderShopping,
  HeaderFavorites,
  HeaderNav,
  HeaderNavDesktop,
  HeaderBurgerNav,
};
